// Nursly Data Rule: All children data is fetched exclusively from Supabase.
// Single source of truth: Supabase > children table.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DbChild, DbZone, ChildInsert, ChildUpdate } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useNurseryId } from "@/hooks/useNurseryId";
import { logAudit } from "@/lib/audit";
import { friendlyError } from "@/lib/friendlyError";
import { attachFundingCodes, saveFundingCode, fundingCodePayload } from "@/lib/fundingCodes";
import { useAuth } from "@/contexts/AuthContext";
import { canAddChild, canArchiveChild } from "@/lib/access";

export type ChildStatusFilter = "active" | "pending" | "archived" | "all";

export function useZones(includeInactive = false) {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["zones", nurseryId, includeInactive ? "all" : "active"],
    enabled: !!nurseryId,
    queryFn: async () => {
      let query = supabase
        .from("zones")
        .select("*")
        .order("sort_order");
      if (!includeInactive) query = query.eq("is_active", true);
      const { data, error } = await query;
      if (error) throw error;
      return data as DbZone[];
    },
  });
}

/**
 * Always fetches every non-deleted child for the nursery. Callers can derive
 * active / pending / archived buckets in memory using `start_date` and
 * `is_archived`. This avoids three round-trips for the Children page tabs.
 */
export function useChildren(_includeArchived = false) {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["children", nurseryId, "all"],
    enabled: !!nurseryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .order("first_name");
      if (error) throw error;
      return attachFundingCodes(data as DbChild[]);
    },
  });
}

/** True if the child's start_date is in the future (i.e. Pending). */
export function isPendingChild(c: Pick<DbChild, "is_archived" | "start_date">, today: string = new Date().toISOString().slice(0, 10)): boolean {
  if (c.is_archived) return false;
  return Boolean(c.start_date && c.start_date > today);
}

/** True if the child is currently attending (started, not archived). */
export function isActiveChild(c: Pick<DbChild, "is_archived" | "start_date">, today: string = new Date().toISOString().slice(0, 10)): boolean {
  if (c.is_archived) return false;
  if (!c.start_date) return true;
  return c.start_date <= today;
}

export function useAddChild() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const nurseryId = useNurseryId();
  const { role } = useAuth();

  return useMutation({
    mutationFn: async (child: ChildInsert) => {
      if (!canAddChild(role)) throw new Error("Your role cannot add a child.");
      // B1-02 safeguard: the `created_by` field MUST match the user who is
      // authenticated in the current session at the moment of creation.
      // Never accept a caller-supplied created_by that doesn't match: that
      // would attribute the profile to the wrong staff member.
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("You must be signed in to create a child profile.");
      }
      const submitted = (child as any).created_by;
      if (submitted && submitted !== user.id) {
        throw new Error(
          "Creator mismatch: the profile could not be attributed to your account. Please refresh and try again.",
        );
      }
      const { data, error } = await supabase
        .from("children")
        .insert({ ...fundingCodePayload(child, role), created_by: user.id, nursery_id: nurseryId } as any)
        .select()
        .single();
      if (error) throw error;
      return data as DbChild;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      toast({ title: "Child added successfully ✅" });
      logAudit({ description: `Added child: ${data.first_name} ${data.last_name}`, recordType: "child", recordId: data.id });
    },
    onError: (error: Error) => {
      toast({ title: "Could not add this child", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useUpdateChild() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { role } = useAuth();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ChildUpdate }) => {
      const { funding_code, ...rest } = fundingCodePayload(updates, role);
      const query = Object.keys(rest).length
        ? supabase.from("children").update(rest).eq("id", id).select()
        : supabase.from("children").select("*").eq("id", id);
      const { data, error } = await query.single();
      if (error) throw error;
      if (funding_code !== undefined && (data as any).nursery_id) {
        await saveFundingCode(id, (data as any).nursery_id as string, funding_code ?? "");
      }
      return data as DbChild;
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      queryClient.invalidateQueries({ queryKey: ["child", data.id] });
      queryClient.invalidateQueries({ queryKey: ["child-audit-log", data.id] });
      toast({ title: "Child updated ✅" });
      const fields = Object.keys(vars.updates || {}).join(", ") || "fields";
      logAudit({ description: `Edited child: ${data.first_name} ${data.last_name}, updated ${fields}`, recordType: "child", recordId: data.id });
    },
    onError: (error: Error) => {
      toast({ title: "Could not save your changes", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useArchiveChild() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { role } = useAuth();

  return useMutation({
    mutationFn: async (input: string | { id: string; reason: string; reason_other?: string }) => {
      if (!canArchiveChild(role)) throw new Error("Only Admins and Owners can archive a child profile.");
      const payload = typeof input === "string"
        ? { id: input, reason: "", reason_other: undefined as string | undefined }
        : input;
      const update: Record<string, unknown> = {
        is_archived: true,
        archived_at: new Date().toISOString(),
      };
      if (payload.reason) update.archive_reason = payload.reason;
      if (payload.reason_other) update.archive_reason_other = payload.reason_other;
      const { data: existing } = await supabase
        .from("children")
        .select("first_name, last_name")
        .eq("id", payload.id)
        .maybeSingle();
      const { error } = await supabase
        .from("children")
        .update(update as any)
        .eq("id", payload.id);
      if (error) throw error;
      const name = existing ? `${existing.first_name} ${existing.last_name}` : "";
      logAudit({
        description: `Archived child${name ? `: ${name}` : ""}${payload.reason ? ` (${payload.reason})` : ""}`,
        recordType: "child",
        recordId: payload.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      toast({ title: "Child archived 📦" });
    },
    onError: (error: Error) => {
      toast({ title: "Could not archive this child", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useUnarchiveChild() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (arg: string | { id: string; pendingStartDate?: string }) => {
      const id = typeof arg === "string" ? arg : arg.id;
      const pendingStartDate = typeof arg === "string" ? undefined : arg.pendingStartDate;
      const today = new Date().toISOString().slice(0, 10);
      if (pendingStartDate !== undefined && !(pendingStartDate > today)) {
        throw new Error("Please choose a start date after today.");
      }
      const patch: Record<string, unknown> = { is_archived: false, archived_at: null, archive_reason: null, archive_reason_other: null };
      if (pendingStartDate) {
        // Restore to Pending: new future start date, no stale schedule.
        patch.start_date = pendingStartDate;
        patch.days_attending = [];
        patch.daily_sessions = null;
      }
      const { error } = await supabase.from("children").update(patch as any).eq("id", id);
      if (error) throw error;
      return { pending: Boolean(pendingStartDate) };
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      toast({ title: r?.pending ? "Child restored to Pending" : "Child restored ✅" });
    },
    onError: (error: Error) => {
      toast({ title: "Could not bring this child back", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useDeleteChild() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("children")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      toast({ title: "Child record deleted 🗑️" });
    },
    onError: (error: Error) => {
      toast({ title: "Could not remove that", description: friendlyError(error), variant: "destructive" });
    },
  });
}
